import { useSlate } from 'slate-react';
import isBlockActive from '@atlas/tool/box/slate/isBlockActive';
import { useMemo } from 'react';
import * as React from 'react';
import classNames from 'classnames';
import toggleBlock from '@atlas/tool/box/slate/toggleBlock';
import type { THotkey } from '@atlas/tool/box/event/hotkey';
import type { Editor } from 'slate';
import styled, { css } from 'styled-components';
import IconButton from '@atlas/uicorn/lib/Control/IconButton';

export default function BlockButton(props: TBlockButtonProps) {
    const editor = useSlate();

    const isActive = ['image', 'video', 'horizontal-line', 'table'].includes(props.block)
        ? false
        : isBlockActive(editor, props.block);

    const tooltip = useMemo(
        () => ({
            content: (
                <>
                    {props.title} {props.hotkey && <kbd>{props.hotkey.toString()}</kbd>}
                </>
            ),
        }),
        [props.title, props.hotkey],
    );

    return (
        <SIconButton
            className={classNames('app-icon-button', {
                'app-icon-button__active': isActive,
            })}
            $active={isActive}
            aria-label={props.title}
            tooltip={tooltip}
            icon={props.icon}
            onMouseDown={(event: React.MouseEvent) => {
                event.preventDefault();
                'action' in props ? props.action(editor) : toggleBlock(editor, props.block);
            }}
        />
    );
}

// #region Styles

export const SIconButton = styled(IconButton)<{
    $active?: boolean;
}>`
    width: 2.8rem;
    justify-content: center;
    flex: 0 0 auto;

    ${(p) =>
        p.$active
            ? css`
                  background: ${(p) => p.theme.color.grey6};
              `
            : ''}
`;

// #endregion

// #region Types

type TBlockButtonProps =
    | {
          block:
              | 'paragraph'
              | 'block-quote'
              | 'heading-one'
              | 'heading-two'
              | 'heading-three'
              | 'code-block'
              | 'callout'
              | 'numbered-list'
              | 'bulleted-list';
          icon: React.ReactElement;
          title: string;
          hotkey?: THotkey;
      }
    | {
          block: 'horizontal-line';
          icon: React.ReactElement;
          title: string;
          hotkey?: THotkey;

          action: (editor: Editor) => unknown;
      }
    | {
          block: 'image';
          title: string;
          icon: React.ReactElement;
          hotkey?: THotkey;

          action: (editor: Editor) => unknown;
      }
    | {
          block: 'video';
          icon: React.ReactElement;
          title: string;
          hotkey?: THotkey;

          action: (editor: Editor) => unknown;
      }
    | {
          block: 'embed';
          icon: React.ReactElement;
          title: string;
          hotkey?: THotkey;

          action: (editor: Editor) => unknown;
      }
    | {
          block: 'table';
          icon: React.ReactElement;
          title: string;
          hotkey?: THotkey;

          action: (editor: Editor) => unknown;
      };

// #endregion
